import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, startWith } from 'rxjs/operators';
import { BreadcrumbService } from './breadcrumb.service';

interface Crumb {
  label: string;
  /** null = aktuelles Segment, nicht klickbar. */
  route: string | null;
}

/** Statisches Path-Token-Mapping. UUIDs (oder andere ID-Pattern) werden ausgelassen. */
const LABELS: Record<string, string> = {
  // Top-Level
  dashboard: 'Dashboard',
  'ki-team': 'KI-Team',
  projekte: 'Projekte',
  organisationen: 'Organisationen',
  profil: 'Mein Profil',
  'meine-einladungen': 'Meine Einladungen',

  // Werkzeuge
  werkzeuge: 'Werkzeuge',
  'nhk-rechner': 'NHK-Rechner',

  // Profil
  'mein-profil': 'Stammdaten',
  sicherheit: 'Sicherheit',
  'api-tokens': 'API-Tokens',

  // Verträge / Leistungsbilder (unter projekte/:id/...)
  vertraege: 'Verträge',
  leistungsbild: 'Leistungsbild',
  leistungsbilder: 'Leistungsbilder',
  feststellungen: 'Leistungsfeststellungen',
  wizard: 'Wizard',
  'neu-wizard': 'Wizard',
  kostenhistorie: 'Kostenhistorie',
  kosten: 'Kosten',

  // Systemeinstellungen (SystemAdmin) + Organisationseinstellungen (MandantAdmin)
  systemeinstellungen: 'Systemeinstellungen',
  organisationseinstellungen: 'Organisationseinstellungen',
  'dms-konfiguration': 'DMS-Konfiguration',
  dokumenttypen: 'Dokumenttypen',
  verknuepfungstypen: 'Verknüpfungstypen',
  wartungsmodus: 'Wartungsmodus',
  'system-admins': 'System-Admins',
  mandanten: 'Mandanten',
  'mein-mandant': 'Mein Mandant',
  'smtp-config': 'SMTP-Konfiguration',
  'email-templates': 'E-Mail-Vorlagen',
  sso: 'SSO-Konfiguration',
  'auth-policy': 'Auth-Policy',
  einladungen: 'Einladungen',
  benutzer: 'Benutzer',
  rollen: 'Rollen',
  'business-units': 'Business Units',
  honorartafeln: 'Honorartafeln',
  'besondere-leistungen': 'Besondere Leistungen',
  'leistungsbilder-admin': 'Leistungsbilder',
  audit: 'Auth-Audit-Log',

  // DMS-System-Admin-Routes
  dms: 'DMS-Verwaltung',
  'storage-pools': 'Storage-Pools',
  'storage-migrationen': 'Storage-Migrationen',
  'audit-logs': 'Audit-Logs',
  'master-key': 'Master-Key',
  'ocr-diagnose': 'OCR-Diagnose',

  // Org-Storage/Encryption/TSA
  speicher: 'Speicher',
  verschluesselung: 'Verschlüsselung',
  tsa: 'TSA',
  'dokument-branding': 'Dokument-Branding',

  // Audit-Log-Tabs
  auth: 'Auth',
  documents: 'Dokumente',

  // Storage-Pool-Detail-Routes + generisches "Neu"-Segment
  new: 'Neu',
  neu: 'Neu',

  // UI-Katalog
  'ui-katalog': 'UI-Katalog',
  buttons: 'Buttons',
  cards: 'Cards',
  dialoge: 'Dialoge',
  toasts: 'Toasts',
  forms: 'Forms & Inputs',
  status: 'Status & Empty',

  // 2FA / SSO Login (falls innerhalb eingeloggter Session erreichbar)
  '2fa': '2FA',
  'magic-link': 'Login-Link per E-Mail',

  // KI-Infrastruktur (L1) + KI-Prüfkriterien (L2)
  ki: 'KI-Infrastruktur',
  verbindungen: 'Verbindungen',
  avatare: 'Avatare',
  tarife: 'Tarife',
  'ki-pruefkriterien': 'KI-Prüfkriterien',
  katalog: 'Prüfkriterien-Katalog',
  auftraege: 'Ableitungs-Aufträge',
  einstellungen: 'Einstellungen',
  konfiguration: 'Konfiguration',
  prozessvisualisierung: 'KI-Prozessvisualisierung',
};

/** Routen-Präfixe, auf denen kein Breadcrumb angezeigt werden soll. */
const HIDE_ON_PREFIX = ['/login', '/password-vergessen', '/password-reset', '/invitation'];

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Breadcrumb-Pfad oben auf jeder Seite (außer Login/Auth).
 *
 * Liest die aktuelle URL aus dem Router und baut die Hierarchie nach
 * Path-Token-Mapping (`LABELS`). UUID-Segments werden übersprungen — die
 * Detail-Seiten haben einen eigenen Page-Header mit dem konkreten Namen.
 *
 * WCAG 2.2 AA: `<nav aria-label="Breadcrumb">`, letztes Item mit
 * `aria-current="page"`, Trennzeichen via CSS (nicht im DOM).
 */
@Component({
  selector: 'app-breadcrumb',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgForOf, NgIf, RouterLink],
  template: `
    <nav *ngIf="visibleCrumbs().length > 0" aria-label="Breadcrumb" class="planaudit-breadcrumb">
      <ol>
        <li *ngFor="let c of visibleCrumbs(); let last = last">
          <ng-container *ngIf="c.route && !last; else currentTpl">
            <a [routerLink]="c.route">{{ c.label }}</a>
          </ng-container>
          <ng-template #currentTpl>
            <span aria-current="page">{{ c.label }}</span>
          </ng-template>
        </li>
      </ol>
    </nav>
  `,
  styles: [
    `
    .planaudit-breadcrumb {
      padding: 0.5rem 1.25rem;
      background: transparent;
      font-size: 0.875rem;
    }
    .planaudit-breadcrumb ol {
      list-style: none;
      display: flex;
      flex-wrap: wrap;
      gap: 0.25rem 0.5rem;
      margin: 0;
      padding: 0;
    }
    .planaudit-breadcrumb li {
      display: inline-flex;
      align-items: center;
      color: var(--cui-body-color, #4a4a55);
    }
    .planaudit-breadcrumb li + li::before {
      content: '/';
      margin-right: 0.5rem;
      color: var(--cui-body-color-secondary, #8a8a92);
      pointer-events: none;
    }
    .planaudit-breadcrumb a {
      color: var(--cui-link-color, #5856d6);
      text-decoration: none;
    }
    .planaudit-breadcrumb a:hover,
    .planaudit-breadcrumb a:focus-visible {
      text-decoration: underline;
    }
    .planaudit-breadcrumb [aria-current="page"] {
      font-weight: 600;
      color: var(--cui-body-color-emphasis, #1a1a24);
    }
  `,
  ],
})
export class BreadcrumbComponent {
  private readonly router = inject(Router);
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly currentUrl = signal<string>(this.router.url);

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        startWith(null),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.currentUrl.set(this.router.url));
  }

  readonly visibleCrumbs = computed<Crumb[]>(() => {
    const url = this.currentUrl();
    const overrides = this.breadcrumbService.labels();

    // Auf Login/Auth-Pfaden nichts anzeigen.
    if (HIDE_ON_PREFIX.some((p) => url === p || url.startsWith(p + '/') || url.startsWith(p + '?'))) {
      return [];
    }

    // Query-String / Fragment abschneiden.
    const path = url.split('?')[0].split('#')[0];
    const segments = path.split('/').filter(Boolean);

    // Auf der reinen Dashboard-/Root-Seite kein Breadcrumb (redundant zum Logo).
    if (segments.length === 0 || (segments.length === 1 && segments[0] === 'dashboard')) {
      return [];
    }

    const crumbs: Crumb[] = [];
    const acc: string[] = [];

    for (const seg of segments) {
      acc.push(seg);

      // 1. Override aus BreadcrumbService — Detail-Pages registrieren hier
      //    den sprechenden Namen für ihre UUID.
      if (overrides[seg]) {
        crumbs.push({ label: overrides[seg], route: '/' + acc.join('/') });
        continue;
      }

      // 2. UUIDs ohne Override überspringen — die Detail-Page-Header zeigen
      //    den konkreten Namen separat.
      if (UUID_REGEX.test(seg)) continue;

      // 3. Statisches Path-Token-Mapping (oder Fallback auf Title-Case).
      const label = LABELS[seg] ?? this.fallbackLabel(seg);
      crumbs.push({ label, route: '/' + acc.join('/') });
    }

    // Letztes Item ist „aktuelle Page" — Route auf null setzen.
    if (crumbs.length > 0) {
      crumbs[crumbs.length - 1] = { ...crumbs[crumbs.length - 1], route: null };
    }
    return crumbs;
  });

  /** Fallback für nicht im LABELS-Mapping enthaltene Tokens: kebab-case → Title-Case. */
  private fallbackLabel(token: string): string {
    return token
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
}
